import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Link } from "@amzn/awsui-components-react";
import {
  ErrorCategorySource,
  ErrorCategorySourcesResult,
} from "../../lusca-search-api/generated-src";
import { InvestigationsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import {
  InvestigationRule,
  predicateValues,
  UNCATEGORIZED,
} from "./investigationUtils";

/**
 * Which CR introduced each DLQ error category. A category is a `.when(...)` rule in
 * PVAccountingCDK's DLQ Glue job; a daily worker task reads that file's history over GitFarm and
 * records the first commit that added each rule (GET /investigations/category-sources).
 */

export const categorySourcesKey = [
  "investigations",
  "category-sources",
] as const;

export function indexSources(
  result: ErrorCategorySourcesResult | undefined
): Record<string, ErrorCategorySource> {
  if (!result?.available) return {};
  return Object.fromEntries(
    (result.categories ?? [])
      .filter((entry) => Boolean(entry?.category))
      .map((entry) => [entry.category, entry])
  );
}

/** Category → source. Stage-independent (one Glue job), so fetched once and kept for an hour. */
export function useErrorCategorySources() {
  return useQuery<Record<string, ErrorCategorySource>>(
    categorySourcesKey,
    async () => {
      const { data } =
        await InvestigationsApiFactory().listErrorCategorySources();
      return indexSources(data);
    },
    { retry: false, staleTime: 60 * 60 * 1000, refetchOnWindowFocus: false }
  );
}

/** The categories a case's rules pin (eq / in on error_category), without "uncategorized". */
export function caseCategories(rules: InvestigationRule[]): string[] {
  const values = rules
    .flatMap((branch) => branch ?? [])
    .filter(
      (p) => (p.field as string) === "error_category" && p.operator !== "not_in"
    )
    .flatMap((p) => predicateValues(p))
    .filter((v) => v && v !== UNCATEGORIZED);
  return Array.from(new Set(values));
}

const formatDate = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? undefined
    : d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

/** One-line description, used as the link's tooltip and the case page's detail text. */
export function sourceSummary(source: ErrorCategorySource): string {
  if (source.migrated) {
    return "Category moved from QuickSight into code in this CR; the change that first created it is not in git.";
  }
  const parts = [
    source.title,
    [source.author, formatDate(source.date)].filter(Boolean).join(", "),
  ].filter(Boolean);
  return parts.join(" · ");
}

const linkTarget = (source: ErrorCategorySource) => ({
  href: source.crUrl || source.commitUrl,
  label: source.cr || source.commit.slice(0, 8),
});

/**
 * Population builder: under the Error category row, "Added in CR-…" for each chosen category
 * that has a known source (details in the link's hover title).
 */
export const BuilderCategorySource: React.FC<{ categories: string[] }> = ({
  categories,
}) => {
  const { data } = useErrorCategorySources();
  const known = categories
    .filter((c) => c && c !== UNCATEGORIZED)
    .map((c) => ({ category: c, source: data?.[c] }))
    .filter((x): x is { category: string; source: ErrorCategorySource } =>
      Boolean(x.source)
    );
  if (!known.length) return null;
  return (
    <div data-testid="builder-category-source">
      {known.map(({ category, source }) => {
        const { href, label } = linkTarget(source);
        return (
          <Box key={category} fontSize="body-s" color="text-body-secondary">
            {known.length > 1 ? `${category}: ` : ""}
            {source.migrated ? "Moved into code in " : "Added in "}
            <span title={sourceSummary(source)}>
              <Link href={href} external fontSize="body-s">
                {label}
              </Link>
            </span>
          </Box>
        );
      })}
    </div>
  );
};

/**
 * Evidence links for a new case: the CR that added each category the rule pins, so the case
 * starts with a pointer to whoever looked at this failure before.
 */
export function categorySourceLinks(
  rules: InvestigationRule[],
  sources: Record<string, ErrorCategorySource> | undefined
): { url: string; label: string }[] {
  if (!sources) return [];
  return caseCategories(rules)
    .map((category) => sources[category])
    .filter((source): source is ErrorCategorySource => Boolean(source))
    .map((source) => {
      const { href, label } = linkTarget(source);
      const how = source.migrated ? "moved into code" : "added";
      return {
        url: href,
        label: `${label}: ${source.category} ${how}${
          source.title && !source.migrated ? ` (${source.title})` : ""
        }`.slice(0, 300),
      };
    });
}
